import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  ArchiveRestore, FolderX, History, Landmark, RefreshCcw, RotateCcw, Search, ShieldAlert,
  ShieldCheck, Trash2, X,
} from "lucide-react";
import { toast } from "sonner";
import { EmptyState, KpiCard, PageHeader, Spinner } from "../../components/common";
import { Alert, AlertDescription, AlertTitle } from "../../components/ui/alert";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Checkbox } from "../../components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Progress } from "../../components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { MultiSelect } from "../../components/ui/multi-select";
import { splitIds, joinIds, namesOf, pruneTerritory } from "../../components/TerritoryFilter";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "../../components/ui/tabs";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { api, errMsg, fmtDate, fmtINR } from "../../lib/api";

const PAGE_SIZE = 25;
const EMPTY_FILTERS = { zone_id: "", ward_id: "", mohalla_id: "", source: "", search: "" };
const SOURCES = [
  ["excel_upload", "Excel Upload"], ["manual_entry", "Manual Entry"],
  ["new_survey", "Survey"], ["reassessment", "Reassessment"], ["third_party", "Third Party"],
];
const SOURCE_CHIP = {
  excel_upload: "bg-sky-50 text-sky-700 border-sky-200",
  third_party: "bg-violet-50 text-violet-800 border-violet-200",
  manual_entry: "bg-slate-100 text-slate-700 border-slate-200",
  new_survey: "bg-emerald-50 text-emerald-700 border-emerald-200",
  reassessment: "bg-emerald-50 text-emerald-700 border-emerald-200",
};
const ACTION_LABEL = {
  property_delete: { label: "Moved to recycle bin", icon: Trash2, cls: "text-amber-700" },
  property_restore: { label: "Restored", icon: ArchiveRestore, cls: "text-emerald-700" },
  property_purge: { label: "Deleted permanently", icon: FolderX, cls: "text-destructive" },
};
const fmtNum = (n) => Number(n || 0).toLocaleString("en-IN");
const fmtDateTime = (s) => {
  if (!s) return "-";
  try {
    return new Date(s).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return s; }
};
const plural = (n, one, many) => `${fmtNum(n)} ${n === 1 ? one : many}`;

function SourceBadge({ source }) {
  const label = (SOURCES.find(([k]) => k === source) || [null, (source || "-").replace(/_/g, " ")])[1];
  return <Badge variant="outline" className={`text-[10px] ${SOURCE_CHIP[source] || ""}`}>{label}</Badge>;
}

function ProtectedBadge({ count }) {
  return (
    <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-700 border-amber-200 gap-1"
      title="Payments/receipts exist — cannot be deleted permanently">
      <Landmark className="h-2.5 w-2.5" /> Protected · {fmtNum(count)} paid
    </Badge>
  );
}

function LocationCell({ p }) {
  return (
    <div className="text-xs leading-tight">
      <p className="font-medium">{p.ward_label || "-"}</p>
      <p className="text-muted-foreground">{[p.zone_label, p.mohalla_label].filter(Boolean).join(" · ") || "—"}</p>
    </div>
  );
}

/** Summarises the active filter for confirmation dialogs (Phase 176: each level may hold several ids). */
function FilterSummary({ filters, geo, testId }) {
  const chips = [];
  const z = namesOf(filters.zone_id, geo.zones, "zone_name", "Zone");
  const w = namesOf(filters.ward_id, geo.wards, "ward_name", "Ward");
  const m = namesOf(filters.mohalla_id, geo.mohallas, "mohalla_name", "Mohalla");
  if (z) chips.push(`Zone${splitIds(filters.zone_id).length > 1 ? "s" : ""}: ${z}`);
  if (w) chips.push(`Ward${splitIds(filters.ward_id).length > 1 ? "s" : ""}: ${w}`);
  if (m) chips.push(`Mohalla${splitIds(filters.mohalla_id).length > 1 ? "s" : ""}: ${m}`);
  if (filters.source) chips.push(`Source: ${(SOURCES.find(([k]) => k === filters.source) || [])[1] || filters.source}`);
  if (filters.search) chips.push(`Search: "${filters.search}"`);
  return (
    <div className="flex flex-wrap gap-1.5" data-testid={testId}>
      {chips.length === 0
        ? <Badge variant="outline" className="text-[10px] bg-destructive/10 text-destructive border-destructive/20">No filter — entire municipality</Badge>
        : chips.map((c) => <Badge key={c} variant="outline" className="text-[10px]">{c}</Badge>)}
    </div>
  );
}

/** Polls a background cleanup job until it finishes. */
function JobProgressDialog({ code, job, onFinished }) {
  const [live, setLive] = useState(job);
  useEffect(() => { setLive(job); }, [job]);
  useEffect(() => {
    if (!job || job.status !== "processing") return;
    let stopped = false;
    let timer;
    const poll = async () => {
      try {
        const r = await api.get(`/platform/municipalities/${code}/cleanup/jobs/${job.id}`, { timeout: 15000 });
        if (stopped) return;
        setLive(r.data);
        if (r.data.status !== "processing") { onFinished(r.data); return; }
      } catch { /* transient */ }
      if (!stopped) timer = setTimeout(poll, 1000);
    };
    poll();
    return () => { stopped = true; clearTimeout(timer); };
  }, [code, job, onFinished]);
  if (!live) return null;
  const processed = live.progress?.processed || 0;
  const pct = live.total ? Math.min(100, Math.round((processed / live.total) * 100)) : 0;
  const verb = { soft_delete: "Moving to recycle bin", restore: "Restoring", purge: "Deleting permanently" }[live.type] || "Working";
  return (
    <Dialog open={live.status === "processing"}>
      <DialogContent className="sm:max-w-md" data-testid="data-cleanup-job-dialog" onInteractOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle className="font-heading">{verb}…</DialogTitle>
          <DialogDescription>{fmtNum(processed)} / {fmtNum(live.total)} records · keep this page open</DialogDescription>
        </DialogHeader>
        <Progress value={pct} className="[&>div]:transition-transform [&>div]:duration-200" data-testid="data-cleanup-job-progress" />
      </DialogContent>
    </Dialog>
  );
}

export default function DataCleanup() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [munis, setMunis] = useState(null);
  const [code, setCode] = useState(searchParams.get("muni") || "");
  const [geo, setGeo] = useState({ zones: [], wards: [], mohallas: [] });
  const [tab, setTab] = useState("active");
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [searchDraft, setSearchDraft] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState({});
  const [activity, setActivity] = useState(null);
  const [busy, setBusy] = useState(false);
  const [job, setJob] = useState(null);
  // dialogs
  const [moveIds, setMoveIds] = useState(null);         // property ids → recycle bin
  const [deleteAllOpen, setDeleteAllOpen] = useState(false);
  const [restoreAllOpen, setRestoreAllOpen] = useState(false);
  const [purgeTarget, setPurgeTarget] = useState(null);  // {ids} | {all: true}
  const [typed, setTyped] = useState("");
  const reloadRef = useRef(() => {});

  const muni = useMemo(() => (munis || []).find((m) => m.code === code) || null, [munis, code]);
  const deleted = tab === "deleted";

  useEffect(() => {
    api.get("/platform/municipalities")
      .then((r) => setMunis((r.data || []).filter((m) => m.is_active !== false)))
      .catch((e) => { toast.error(errMsg(e)); setMunis([]); });
  }, []);

  // debounce search
  useEffect(() => {
    const t = setTimeout(() => {
      setFilters((f) => (f.search === searchDraft ? f : { ...f, search: searchDraft }));
      setPage(1);
    }, 350);
    return () => clearTimeout(t);
  }, [searchDraft]);

  const loadActivity = useCallback(() => {
    if (!code) return;
    api.get(`/platform/municipalities/${code}/cleanup/activity`, { params: { limit: 12 } })
      .then((r) => setActivity(r.data)).catch(() => setActivity([]));
  }, [code]);

  const selectMuni = (c) => {
    setCode(c);
    setSearchParams(c ? { muni: c } : {});
    setTab("active"); setFilters(EMPTY_FILTERS); setSearchDraft(""); setPage(1); setSelected({}); setData(null); setActivity(null);
  };

  useEffect(() => {
    if (!code) { setGeo({ zones: [], wards: [], mohallas: [] }); return; }
    api.get(`/platform/municipalities/${code}/geo`).then((r) => setGeo(r.data)).catch((e) => toast.error(errMsg(e)));
    loadActivity();
  }, [code, loadActivity]);

  const load = useCallback(() => {
    if (!code) return;
    setLoading(true); setError(null);
    const params = { status: tab, page, limit: PAGE_SIZE };
    Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
    api.get(`/platform/municipalities/${code}/properties`, { params, timeout: 60000 })
      .then((r) => { setData(r.data); setSelected({}); })
      .catch((e) => { setError(errMsg(e)); toast.error(errMsg(e)); })
      .finally(() => setLoading(false));
  }, [code, tab, filters, page]);
  useEffect(() => { load(); }, [load]);
  reloadRef.current = () => { load(); loadActivity(); };

  const items = data?.items || [];
  const total = data?.total || 0;
  const counts = data?.counts || {};
  const selectedIds = Object.keys(selected).filter((k) => selected[k]);
  const selectedItems = items.filter((p) => selected[p.id]);
  const selectedProtected = selectedItems.filter((p) => p.protected).length;
  const allChecked = items.length > 0 && items.every((p) => selected[p.id]);
  const hasFilter = Object.values(filters).some(Boolean);

  // Phase 176: zone / ward / mohalla filters are multi-select (comma-joined id lists); children cascade from the parents
  const zoneSel = useMemo(() => splitIds(filters.zone_id), [filters.zone_id]);
  const wardSel = useMemo(() => splitIds(filters.ward_id), [filters.ward_id]);
  const mohallaSel = useMemo(() => splitIds(filters.mohalla_id), [filters.mohalla_id]);
  const wardOptions = useMemo(() => geo.wards.filter((w) => !zoneSel.length || zoneSel.includes(w.zone_id)), [geo, zoneSel]);
  const mohallaOptions = useMemo(() => {
    const wardScope = wardSel.length ? wardSel : (zoneSel.length ? wardOptions.map((w) => w.id) : null);
    return wardScope ? geo.mohallas.filter((m) => wardScope.includes(m.ward_id)) : geo.mohallas;
  }, [geo, wardSel, zoneSel, wardOptions]);
  const zoneNameOf = (id) => geo.zones.find((z) => z.id === id)?.zone_name;
  const wardNameOf = (id) => geo.wards.find((w) => w.id === id)?.ward_name;

  const setFilter = (patch) => { setFilters((f) => ({ ...f, ...patch })); setPage(1); setSelected({}); };
  const setTerritory = (patch) => {
    const next = pruneTerritory(geo, { zoneId: filters.zone_id, wardId: filters.ward_id, mohallaId: filters.mohalla_id, ...patch });
    setFilter({ zone_id: next.zoneId, ward_id: next.wardId, mohalla_id: next.mohallaId });
  };
  const resetFilters = () => { setFilters(EMPTY_FILTERS); setSearchDraft(""); setPage(1); setSelected({}); };

  // ---- actions ----
  const summarize = (j) => {
    const r = j.result || {};
    if (j.type === "soft_delete") return `${plural(r.deleted || 0, "property", "properties")} moved to the recycle bin — restorable anytime`;
    if (j.type === "restore") return `${plural(r.restored || 0, "property", "properties")} restored` + (r.conflicts ? ` · ${r.conflicts} skipped (PIN already in use)` : "");
    return `${plural(r.purged || 0, "record", "records")} deleted permanently` + (r.skipped_protected ? ` · ${r.skipped_protected} skipped (protected — have payments)` : "");
  };
  const onJobFinished = useCallback((j) => {
    setJob(null);
    if (j.status === "failed") toast.error(j.error || "The cleanup job failed on the server.");
    else toast.success(summarize(j));
    reloadRef.current();
  }, []);

  const runAction = async (kind, body) => {
    setBusy(true);
    try {
      const r = await api.post(`/platform/municipalities/${code}/cleanup/${kind}`, body, { timeout: 120000 });
      setMoveIds(null); setDeleteAllOpen(false); setRestoreAllOpen(false); setPurgeTarget(null); setTyped("");
      if (r.data.status === "processing") { setJob(r.data); return; }
      onJobFinished(r.data);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const purgeCount = purgeTarget?.all ? counts.bin_matching || 0 : (purgeTarget?.ids?.length || 0);
  const purgeProtected = purgeTarget?.all ? counts.protected_bin || 0
    : items.filter((p) => purgeTarget?.ids?.includes(p.id) && p.protected).length;
  const codeMatches = !!muni && typed.trim().toLowerCase() === muni.code.toLowerCase();
  const deleteTyped = typed.trim().toUpperCase() === "DELETE";

  return (
    <div className="space-y-5 pb-24" data-testid="super-data-cleanup-page">
      <PageHeader title="Data Cleanup"
        subtitle="Browse a municipality's register by Zone → Ward → Mohalla, move records to its Recycle Bin, restore them, or delete them permanently">
        <Button variant="outline" size="sm" onClick={() => reloadRef.current()} disabled={!code || loading} data-testid="data-cleanup-refresh-button">
          <RefreshCcw className="h-4 w-4 mr-1.5" /> Refresh
        </Button>
      </PageHeader>

      {/* ---- municipality ---- */}
      <Card className="shadow-sm">
        <CardContent className="p-4 sm:p-5 flex flex-col sm:flex-row sm:items-end gap-4">
          <div className="space-y-1.5 flex-1 max-w-md">
            <Label>Municipality</Label>
            {!munis ? <Spinner label="Loading municipalities..." /> : (
              <Select value={code} onValueChange={selectMuni}>
                <SelectTrigger data-testid="data-cleanup-muni-select"><SelectValue placeholder="Select a municipality" /></SelectTrigger>
                <SelectContent>
                  {munis.map((m) => (
                    <SelectItem key={m.code} value={m.code} data-testid={`data-cleanup-muni-option-${m.code}`}>
                      {m.name} <span className="text-muted-foreground font-mono text-xs">({m.code})</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
          <div className="text-xs bg-destructive/10 text-destructive rounded-md px-3 py-2 flex gap-2 flex-1" data-testid="data-cleanup-warning">
            <ShieldAlert className="h-4 w-4 shrink-0 mt-0.5" />
            <span>
              Deleting moves records to the <span className="font-semibold">Recycle Bin</span> (restorable).
              Only <span className="font-semibold">Delete permanently</span> in the Recycle Bin is irreversible — and it always
              skips properties that have payments or receipts.
            </span>
          </div>
        </CardContent>
      </Card>

      {!code ? (
        <Card className="shadow-sm"><CardContent className="p-0">
          <EmptyState title="Pick a municipality to begin" subtitle="You'll be able to filter its property register by Zone, Ward and Mohalla before deleting or restoring anything." />
        </CardContent></Card>
      ) : (
        <>
          {/* ---- filter bar ---- */}
          <Card className="shadow-sm md:sticky md:top-2 z-10 bg-background/85 backdrop-blur-md" data-testid="data-cleanup-filter-bar">
            <CardContent className="p-3 sm:p-4 flex flex-wrap gap-2 items-center">
              <MultiSelect options={geo.zones.map((z) => ({ value: z.id, label: z.zone_name }))} value={zoneSel}
                onChange={(ids) => setTerritory({ zoneId: joinIds(ids) })}
                allLabel="All zones" noun="zone" placeholder="Search zones…" className="w-44 h-9" testId="data-cleanup-zone-select" />
              <MultiSelect options={wardOptions.map((w) => ({ value: w.id, label: w.ward_name, group: zoneSel.length === 1 ? undefined : zoneNameOf(w.zone_id) }))}
                value={wardSel} onChange={(ids) => setTerritory({ wardId: joinIds(ids) })}
                allLabel="All wards" noun="ward" placeholder="Search wards…" className="w-44 h-9" testId="data-cleanup-ward-select" />
              <MultiSelect options={mohallaOptions.map((m) => ({ value: m.id, label: m.mohalla_name, group: wardSel.length === 1 ? undefined : wardNameOf(m.ward_id) }))}
                value={mohallaSel} onChange={(ids) => setTerritory({ mohallaId: joinIds(ids) })}
                allLabel="All mohallas" noun="mohalla" placeholder="Search mohallas…" className="w-44 h-9" testId="data-cleanup-mohalla-select" />
              <Select value={filters.source || "all"} onValueChange={(v) => setFilter({ source: v === "all" ? "" : v })}>
                <SelectTrigger className="w-40 h-9" data-testid="data-cleanup-source-select"><SelectValue placeholder="Source" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All sources</SelectItem>
                  {SOURCES.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
                </SelectContent>
              </Select>
              <div className="relative flex-1 min-w-[200px]">
                <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input className="pl-9 h-9" placeholder="Search by PIN / owner / address" value={searchDraft}
                  onChange={(e) => setSearchDraft(e.target.value)} data-testid="data-cleanup-search-input" />
              </div>
              <Button variant="ghost" size="sm" className="h-9" onClick={resetFilters} disabled={!hasFilter && !searchDraft} data-testid="data-cleanup-reset-filters-button">
                <X className="h-3.5 w-3.5 mr-1" /> Reset
              </Button>
            </CardContent>
          </Card>

          {/* ---- KPI tiles ---- */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <KpiCard label={hasFilter ? "Active properties in filter" : "Active properties"} value={counts.active_matching}
              icon={ShieldCheck} testId="data-cleanup-count-active" />
            <KpiCard label={hasFilter ? "In recycle bin (this filter)" : "In recycle bin"} value={counts.bin_matching}
              icon={Trash2} sub="Restorable until deleted permanently" testId="data-cleanup-count-recycle-bin" />
            <KpiCard label={deleted ? "Protected in recycle bin" : "Protected (have payments)"}
              value={deleted ? counts.protected_bin : counts.protected_active}
              icon={Landmark} sub="Never deleted permanently" testId="data-cleanup-count-protected" />
          </div>

          {/* ---- tabs + table ---- */}
          <div className="grid lg:grid-cols-3 gap-5">
            <Card className="shadow-sm lg:col-span-2">
              <CardHeader className="pb-3 flex-row items-center justify-between space-y-0 gap-2 flex-wrap">
                <Tabs value={tab} onValueChange={(v) => { setTab(v); setPage(1); setSelected({}); }}>
                  <TabsList>
                    <TabsTrigger value="active" data-testid="data-cleanup-tab-active">Active register</TabsTrigger>
                    <TabsTrigger value="deleted" data-testid="data-cleanup-tab-recycle-bin">
                      Recycle Bin {counts.bin_matching > 0 && <Badge variant="secondary" className="ml-1.5 text-[10px]">{fmtNum(counts.bin_matching)}</Badge>}
                    </TabsTrigger>
                  </TabsList>
                </Tabs>
                <p className="text-xs text-muted-foreground tabular-nums" data-testid="data-cleanup-total">{fmtNum(total)} matching</p>
              </CardHeader>
              <CardContent className="p-0">
                {error ? (
                  <Alert variant="destructive" className="m-4" data-testid="data-cleanup-error">
                    <AlertTitle>Could not load properties</AlertTitle>
                    <AlertDescription className="flex items-center justify-between gap-3">
                      <span>{error}</span>
                      <Button size="sm" variant="outline" onClick={load} data-testid="data-cleanup-retry-button">Retry</Button>
                    </AlertDescription>
                  </Alert>
                ) : loading && !data ? <Spinner label="Loading properties…" /> : items.length === 0 ? (
                  deleted
                    ? <EmptyState title="Recycle Bin is empty for this filter" subtitle="Deleted properties appear here until they are restored or permanently deleted." />
                    : <EmptyState title="No properties match this filter" subtitle="Try clearing the Mohalla or search, or switch to the Recycle Bin." />
                ) : (
                  <>
                    <div className={`overflow-x-auto border-t ${loading ? "opacity-60" : ""}`}>
                      <Table data-testid="data-cleanup-table">
                        <TableHeader>
                          <TableRow>
                            <TableHead className="w-10">
                              <Checkbox checked={allChecked} data-testid="data-cleanup-select-all-page"
                                onCheckedChange={(v) => { const n = {}; items.forEach((p) => { n[p.id] = !!v; }); setSelected(n); }} />
                            </TableHead>
                            <TableHead>PIN</TableHead>
                            <TableHead>Owner</TableHead>
                            <TableHead>Location</TableHead>
                            {deleted ? (
                              <>
                                <TableHead className="hidden md:table-cell">Deleted</TableHead>
                                <TableHead>Status</TableHead>
                              </>
                            ) : (
                              <>
                                <TableHead>Source</TableHead>
                                <TableHead className="text-center hidden md:table-cell">Demands</TableHead>
                                <TableHead className="text-right hidden lg:table-cell">Balance</TableHead>
                                <TableHead>Status</TableHead>
                              </>
                            )}
                            <TableHead className="text-right">Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {items.map((p) => (
                            <TableRow key={p.id} data-testid={`data-cleanup-row-${p.id}`} className={selected[p.id] ? "bg-accent/40" : ""}>
                              <TableCell>
                                <Checkbox checked={!!selected[p.id]} data-testid={`data-cleanup-select-row-${p.id}`}
                                  onCheckedChange={(v) => setSelected((s) => ({ ...s, [p.id]: !!v }))} />
                              </TableCell>
                              <TableCell className="font-mono text-xs">{p.pin}</TableCell>
                              <TableCell className="text-sm">
                                <p>{p.owner_name || "-"}</p>
                                {p.address && <p className="text-[11px] text-muted-foreground truncate max-w-[200px]" title={p.address}>{p.address}</p>}
                              </TableCell>
                              <TableCell><LocationCell p={p} /></TableCell>
                              {deleted ? (
                                <>
                                  <TableCell className="hidden md:table-cell text-xs">
                                    <p>{fmtDateTime(p.deleted_at)}</p>
                                    <p className="text-muted-foreground">{p.deleted_by_name || "-"} · {p.deleted_via === "super_admin" ? "Super Admin" : p.deleted_via === "third_party_bulk_undo" ? "Import undo" : "Admin"}</p>
                                  </TableCell>
                                  <TableCell>
                                    {p.protected ? <ProtectedBadge count={p.payments_count} />
                                      : <Badge variant="outline" className="text-[10px] bg-slate-100 text-slate-600 border-slate-200">In recycle bin</Badge>}
                                  </TableCell>
                                </>
                              ) : (
                                <>
                                  <TableCell><SourceBadge source={p.source} /></TableCell>
                                  <TableCell className="text-center text-sm tabular-nums hidden md:table-cell">{p.demand_count}</TableCell>
                                  <TableCell className="text-right text-sm tabular-nums hidden lg:table-cell">{fmtINR(p.balance)}</TableCell>
                                  <TableCell>{p.protected ? <ProtectedBadge count={p.payments_count} /> : <span className="text-xs text-muted-foreground">—</span>}</TableCell>
                                </>
                              )}
                              <TableCell className="text-right whitespace-nowrap">
                                {deleted ? (
                                  <div className="inline-flex gap-1">
                                    <Button variant="outline" size="sm" className="h-7 text-xs" disabled={busy}
                                      onClick={() => runAction("restore", { ids: [p.id] })} data-testid={`data-cleanup-row-restore-${p.id}`}>
                                      <ArchiveRestore className="h-3 w-3 mr-1" /> Restore
                                    </Button>
                                    <span title={p.protected ? "Payments/receipts exist — cannot delete permanently" : undefined}>
                                      <Button variant="outline" size="sm" disabled={busy || p.protected}
                                        className="h-7 text-xs text-destructive border-destructive/30 hover:bg-destructive/10 hover:text-destructive"
                                        onClick={() => { setTyped(""); setPurgeTarget({ ids: [p.id] }); }} data-testid={`data-cleanup-row-delete-permanently-${p.id}`}>
                                        <FolderX className="h-3 w-3 mr-1" /> Delete permanently
                                      </Button>
                                    </span>
                                  </div>
                                ) : (
                                  <Button variant="outline" size="sm" disabled={busy}
                                    className="h-7 text-xs text-destructive border-destructive/30 hover:bg-destructive/10 hover:text-destructive"
                                    onClick={() => setMoveIds([p.id])} data-testid={`data-cleanup-row-move-to-bin-${p.id}`}>
                                    <Trash2 className="h-3 w-3 mr-1" /> Move to recycle bin
                                  </Button>
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                    <div className="flex items-center justify-between px-4 py-3 border-t">
                      <p className="text-xs text-muted-foreground tabular-nums" data-testid="data-cleanup-pagination-label">
                        Page {page} of {Math.max(1, Math.ceil(total / PAGE_SIZE))} · {fmtNum(total)} total
                      </p>
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm" disabled={page === 1 || loading} onClick={() => { setPage(page - 1); setSelected({}); }} data-testid="data-cleanup-prev-page">Previous</Button>
                        <Button variant="outline" size="sm" disabled={page * PAGE_SIZE >= total || loading} onClick={() => { setPage(page + 1); setSelected({}); }} data-testid="data-cleanup-next-page">Next</Button>
                      </div>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>

            {/* ---- activity ---- */}
            <Card className="shadow-sm" data-testid="data-cleanup-activity-log-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-heading flex items-center gap-2"><History className="h-4 w-4" /> Recent cleanup activity</CardTitle>
                <p className="text-xs text-muted-foreground">Latest property actions by Super Admins for {muni?.name || code}</p>
              </CardHeader>
              <CardContent className="p-0">
                {!activity ? <Spinner label="Loading activity…" /> : activity.length === 0 ? (
                  <p className="text-sm text-muted-foreground px-4 pb-5" data-testid="data-cleanup-activity-empty">No cleanup actions yet for this municipality.</p>
                ) : (
                  <ul className="divide-y">
                    {activity.map((a, i) => {
                      const meta = ACTION_LABEL[a.type] || { label: a.type, icon: History, cls: "" };
                      const Icon = meta.icon;
                      const r = a.result || {};
                      const n = r.count ?? r.deleted ?? r.restored ?? r.purged ?? 0;
                      return (
                        <li key={a.id} className="px-4 py-2.5 flex items-start gap-3" data-testid={`data-cleanup-activity-row-${i}`}>
                          <span className={`mt-0.5 ${meta.cls}`}><Icon className="h-4 w-4" /></span>
                          <span className="min-w-0 flex-1">
                            <span className="block text-sm"><span className="font-medium tabular-nums">{fmtNum(n)}</span> · {meta.label}
                              {a.type === "property_purge" && r.skipped_protected ? <span className="text-xs text-muted-foreground"> · {r.skipped_protected} protected skipped</span> : null}
                              {a.all_matching && <Badge variant="outline" className="ml-1.5 text-[10px]">filter-wide</Badge>}
                            </span>
                            <span className="block text-[11px] text-muted-foreground">{a.by_name || "Super Admin"} · {fmtDateTime(a.at)}</span>
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </CardContent>
            </Card>
          </div>

          {/* ---- sticky bulk action bar ---- */}
          {data && (total > 0 || selectedIds.length > 0) && (
            <div className="sticky bottom-3 z-20" data-testid="data-cleanup-bulk-bar">
              <Card className="shadow-lg border-border/80 bg-card/95 backdrop-blur-md">
                <CardContent className="p-3 flex flex-wrap items-center justify-between gap-2">
                  <p className="text-sm tabular-nums" data-testid="data-cleanup-selected-count">
                    {selectedIds.length > 0
                      ? <><span className="font-semibold">{fmtNum(selectedIds.length)}</span> selected on this page{selectedProtected > 0 && deleted ? <span className="text-xs text-muted-foreground"> · {selectedProtected} protected</span> : null}</>
                      : <span className="text-muted-foreground">No rows selected · filter matches <span className="font-semibold text-foreground">{fmtNum(total)}</span></span>}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {!deleted ? (
                      <>
                        {selectedIds.length > 0 && (
                          <Button variant="outline" size="sm" disabled={busy} onClick={() => setMoveIds(selectedIds)}
                            className="text-destructive border-destructive/40 hover:bg-destructive/10 hover:text-destructive"
                            data-testid="data-cleanup-move-selected-button">
                            <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Move selected to recycle bin ({fmtNum(selectedIds.length)})
                          </Button>
                        )}
                        <Button variant="destructive" size="sm" disabled={busy || total === 0} onClick={() => { setTyped(""); setDeleteAllOpen(true); }}
                          data-testid="data-cleanup-delete-all-matching-button">
                          <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Delete ALL matching filter ({fmtNum(total)})
                        </Button>
                      </>
                    ) : (
                      <>
                        {selectedIds.length > 0 && (
                          <>
                            <Button variant="outline" size="sm" disabled={busy} onClick={() => runAction("restore", { ids: selectedIds })}
                              data-testid="data-cleanup-restore-selected-button">
                              <ArchiveRestore className="h-3.5 w-3.5 mr-1.5" /> Restore selected ({fmtNum(selectedIds.length)})
                            </Button>
                            <Button variant="outline" size="sm" disabled={busy || selectedProtected === selectedIds.length}
                              onClick={() => { setTyped(""); setPurgeTarget({ ids: selectedIds }); }}
                              className="text-destructive border-destructive/40 hover:bg-destructive/10 hover:text-destructive"
                              title={selectedProtected === selectedIds.length ? "All selected records are protected (have payments)" : undefined}
                              data-testid="data-cleanup-delete-permanently-button">
                              <FolderX className="h-3.5 w-3.5 mr-1.5" /> Delete permanently ({fmtNum(selectedIds.length - selectedProtected)})
                            </Button>
                          </>
                        )}
                        <Button variant="outline" size="sm" disabled={busy || total === 0} onClick={() => setRestoreAllOpen(true)}
                          data-testid="data-cleanup-restore-all-matching-button">
                          <RotateCcw className="h-3.5 w-3.5 mr-1.5" /> Restore ALL matching ({fmtNum(total)})
                        </Button>
                        <Button variant="destructive" size="sm" disabled={busy || total === 0 || (counts.protected_bin || 0) >= total}
                          onClick={() => { setTyped(""); setPurgeTarget({ all: true }); }}
                          data-testid="data-cleanup-delete-all-permanently-button">
                          <FolderX className="h-3.5 w-3.5 mr-1.5" /> Delete ALL matching permanently ({fmtNum(Math.max(0, total - (counts.protected_bin || 0)))})
                        </Button>
                      </>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </>
      )}

      {/* ---- dialog: move to recycle bin ---- */}
      <AlertDialog open={!!moveIds} onOpenChange={(o) => !o && setMoveIds(null)}>
        <AlertDialogContent data-testid="data-cleanup-move-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Move {plural(moveIds?.length || 0, "property record", "property records")} to the Recycle Bin?</AlertDialogTitle>
            <AlertDialogDescription>
              They leave the active register together with their demands and collector assignments, and can be restored
              from the Recycle Bin at any time. Payment records and receipts are never deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="data-cleanup-dialog-cancel-button">Cancel</AlertDialogCancel>
            <AlertDialogAction disabled={busy} onClick={() => runAction("soft-delete", { ids: moveIds })}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="data-cleanup-dialog-confirm-button">
              {busy ? "Moving…" : "Move to recycle bin"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* ---- dialog: delete ALL matching (typed municipality code) ---- */}
      <Dialog open={deleteAllOpen} onOpenChange={(o) => { if (!o) { setDeleteAllOpen(false); setTyped(""); } }}>
        <DialogContent className="sm:max-w-lg" data-testid="data-cleanup-delete-all-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Delete ALL matching records for {muni?.name}?</DialogTitle>
            <DialogDescription>
              This moves <span className="font-semibold text-foreground tabular-nums">{fmtNum(total)}</span> record(s) matching the current
              filter into the Recycle Bin. They stay restorable. This action is logged and the municipality's admins are notified.
            </DialogDescription>
          </DialogHeader>
          <FilterSummary filters={filters} geo={geo} testId="data-cleanup-delete-all-filter-summary" />
          {!hasFilter && (
            <Alert variant="destructive" data-testid="data-cleanup-delete-all-nofilter-warning">
              <ShieldAlert className="h-4 w-4" />
              <AlertTitle>No filter applied</AlertTitle>
              <AlertDescription>This would move the municipality's <strong>entire</strong> active register to the Recycle Bin. Narrow the filter first unless that is intended.</AlertDescription>
            </Alert>
          )}
          <div className="space-y-1.5">
            <Label htmlFor="dc-confirm-code">Type the municipality code <span className="font-mono">{muni?.code}</span> to confirm</Label>
            <Input id="dc-confirm-code" value={typed} onChange={(e) => setTyped(e.target.value)} placeholder={muni?.code} autoComplete="off"
              className="font-mono" data-testid="data-cleanup-typed-confirm-input" />
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => { setDeleteAllOpen(false); setTyped(""); }} data-testid="data-cleanup-dialog-cancel-button">Cancel</Button>
            <Button variant="destructive" disabled={!codeMatches || busy} data-testid="data-cleanup-dialog-confirm-button"
              onClick={() => runAction("soft-delete", { all_matching: true, filters, confirm_code: typed.trim() })}>
              <Trash2 className="h-4 w-4 mr-2" /> {busy ? "Moving…" : `Yes, move all ${fmtNum(total)} to recycle bin`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ---- dialog: restore ALL matching ---- */}
      <AlertDialog open={restoreAllOpen} onOpenChange={setRestoreAllOpen}>
        <AlertDialogContent data-testid="data-cleanup-restore-all-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Restore all {fmtNum(total)} matching record(s)?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2">
                <p>Every Recycle Bin record matching the current filter returns to the active register with its demands and assignments. Records whose PIN is now used by another property are skipped and reported.</p>
                <FilterSummary filters={filters} geo={geo} testId="data-cleanup-restore-all-filter-summary" />
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="data-cleanup-dialog-cancel-button">Cancel</AlertDialogCancel>
            <AlertDialogAction disabled={busy} onClick={() => runAction("restore", { all_matching: true, filters })} data-testid="data-cleanup-dialog-confirm-button">
              <ArchiveRestore className="h-4 w-4 mr-2" /> {busy ? "Restoring…" : "Restore all"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* ---- dialog: delete permanently (typed DELETE) ---- */}
      <Dialog open={!!purgeTarget} onOpenChange={(o) => { if (!o) { setPurgeTarget(null); setTyped(""); } }}>
        <DialogContent className="sm:max-w-lg" data-testid="data-cleanup-delete-permanently-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-destructive flex items-center gap-2"><FolderX className="h-4 w-4" /> Delete permanently? This cannot be undone.</DialogTitle>
            <DialogDescription>
              This permanently deletes <span className="font-semibold text-foreground tabular-nums">{fmtNum(Math.max(0, purgeCount - purgeProtected))}</span> record(s)
              from the Recycle Bin — including their saved demands, assignments and survey data. Payment records and receipts are never deleted.
            </DialogDescription>
          </DialogHeader>
          {purgeTarget?.all && <FilterSummary filters={filters} geo={geo} testId="data-cleanup-purge-filter-summary" />}
          {purgeProtected > 0 && (
            <Alert data-testid="data-cleanup-purge-protected-note">
              <Landmark className="h-4 w-4" />
              <AlertTitle>{plural(purgeProtected, "protected record", "protected records")} will be skipped</AlertTitle>
              <AlertDescription>Properties with payments or receipts stay in the Recycle Bin — restore them or keep them there.</AlertDescription>
            </Alert>
          )}
          <div className="space-y-1.5">
            <Label htmlFor="dc-confirm-delete">Type <span className="font-mono">DELETE</span> to enable permanent deletion</Label>
            <Input id="dc-confirm-delete" value={typed} onChange={(e) => setTyped(e.target.value)} placeholder="DELETE" autoComplete="off"
              className="font-mono" data-testid="data-cleanup-typed-confirm-input" />
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => { setPurgeTarget(null); setTyped(""); }} data-testid="data-cleanup-dialog-cancel-button">Cancel</Button>
            <Button variant="destructive" disabled={!deleteTyped || busy || purgeCount - purgeProtected <= 0} data-testid="data-cleanup-dialog-confirm-button"
              onClick={() => runAction("purge", purgeTarget?.all
                ? { all_matching: true, filters, confirm_text: typed.trim() }
                : { ids: purgeTarget.ids, confirm_text: typed.trim() })}>
              <FolderX className="h-4 w-4 mr-2" /> {busy ? "Deleting…" : "Delete permanently"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <JobProgressDialog code={code} job={job} onFinished={onJobFinished} />
    </div>
  );
}
